import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  type DragEndEvent,
  type DragStartEvent,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { applicationsApi } from "../api/applications";
import { APPLICATION_STATUSES, type Application, type ApplicationStatus } from "../api/types";
import { STATUS_LABELS } from "../components/StatusBadge";
import { Spinner } from "../components/ui";
import { FitScore } from "./Applications";

const KEY = ["applications"];

/**
 * Applications grouped by stage, with drag to move between columns.
 *
 * The drop is optimistic: the card moves immediately and the PATCH follows,
 * with the previous list restored if it fails. Waiting for a round trip before
 * the card moves feels broken.
 */
export default function Board() {
  const queryClient = useQueryClient();
  const [dragging, setDragging] = useState<Application | null>(null);

  const { data, isPending } = useQuery({ queryKey: KEY, queryFn: () => applicationsApi.list() });

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ApplicationStatus }) =>
      applicationsApi.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: KEY });
      const previous = queryClient.getQueryData<Application[]>(KEY);
      queryClient.setQueryData<Application[]>(KEY, (current) =>
        (current ?? []).map((a) => (a.id === id ? { ...a, status } : a)),
      );
      return { previous };
    },
    onError: (_error, _variables, context) =>
      queryClient.setQueryData(KEY, context?.previous),
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });

  const columns = useMemo(() => {
    const grouped = new Map<ApplicationStatus, Application[]>(
      APPLICATION_STATUSES.map((status) => [status, []]),
    );
    for (const application of data ?? []) {
      grouped.get(application.status)?.push(application);
    }
    return grouped;
  }, [data]);

  // A short activation distance, so a plain click on a card still navigates.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function onDragStart(event: DragStartEvent) {
    setDragging((data ?? []).find((a) => a.id === event.active.id) ?? null);
  }

  function onDragEnd(event: DragEndEvent) {
    setDragging(null);
    const status = event.over?.id as ApplicationStatus | undefined;
    const application = (data ?? []).find((a) => a.id === event.active.id);
    if (!status || !application || application.status === status) return;
    move.mutate({ id: application.id, status });
  }

  if (isPending) return <Spinner label="Loading board…" />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Board</h1>
        <p className="mt-1 text-sm text-gray-600">
          Drag an application to move it through the stages.
        </p>
      </header>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {APPLICATION_STATUSES.map((status) => (
            <Column key={status} status={status} applications={columns.get(status) ?? []} />
          ))}
        </div>

        <DragOverlay>{dragging && <CardBody application={dragging} overlay />}</DragOverlay>
      </DndContext>
    </div>
  );
}

function Column({
  status,
  applications,
}: {
  status: ApplicationStatus;
  applications: Application[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <section
      ref={setNodeRef}
      className={
        "flex min-w-40 flex-1 flex-col rounded-lg border p-3 transition-colors " +
        (isOver ? "border-accent-500 bg-accent-50" : "border-gray-200 bg-gray-50")
      }
    >
      <h2 className="mb-3 flex items-baseline justify-between text-sm font-medium text-gray-800">
        {STATUS_LABELS[status]}
        <span className="text-xs text-gray-400">{applications.length}</span>
      </h2>

      <div className="flex flex-col gap-2">
        {applications.map((application) => (
          <DraggableCard key={application.id} application={application} />
        ))}
        {applications.length === 0 && (
          <p className="py-6 text-center text-xs text-gray-400">Nothing here</p>
        )}
      </div>
    </section>
  );
}

function DraggableCard({ application }: { application: Application }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: application.id });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? "opacity-40" : undefined}
    >
      <Link to={`/applications/${application.id}`} className="block">
        <CardBody application={application} />
      </Link>
    </div>
  );
}

function CardBody({ application, overlay }: { application: Application; overlay?: boolean }) {
  return (
    <div
      className={
        "rounded-md border border-gray-200 bg-surface p-3 shadow-xs " +
        (overlay ? "rotate-2 shadow-md" : "hover:border-accent-500")
      }
    >
      <p className="truncate text-sm font-medium text-gray-900">
        {application.role || "Untitled role"}
      </p>
      <p className="mt-0.5 truncate text-xs text-gray-600">{application.company || "—"}</p>
      {application.fit_score !== null && (
        <div className="mt-2">
          <FitScore score={application.fit_score} />
        </div>
      )}
    </div>
  );
}
